import type { ThemeName } from "../lib/theme";

export type PropertyKind = "residential" | "land" | "improved_land";
export type PropertyStatus = "For Sale" | "Pending" | "Sold";

export type GalleryCategoryKey =
    | "aerial"
    | "featured"
    | "indoors"
    | "other"
    | "videos"
    | "wildlife"
    | "water";

export type GalleryCategories = Partial<Record<GalleryCategoryKey, string[]>>;

export type ListingCommon = {
    slug: string;
    shortId: string;
    theme?: ThemeName;
    title: string;
    location: string;
    priceLabel: string;
    status: PropertyStatus;
    acres?: number;
    coords?: { lat: number; lng: number };
    tags: string[];
    description: string;
    features: string[];
    image: string;
    galleries?: GalleryCategories;
};

export type ResidentialProperty = ListingCommon & {
    kind: "residential";
    beds: number;
    baths: number;
    sqft: number;
};

export type LandProperty = ListingCommon & {
    kind: "land";
    acres: number;
    beds?: never;
    baths?: never;
    sqft?: never;
};

export type ImprovedLandProperty = ListingCommon & {
    kind: "improved_land";
    acres: number;
    beds?: number;
    baths?: number;
    sqft?: number;
};

export type Property =
    | ResidentialProperty
    | LandProperty
    | ImprovedLandProperty;

export const PROPERTIES: Property[] = [
    {
        kind: "improved_land",
        slug: "wallowa-mountain-retreat",
        title: "Wallowa Mountain Retreat",
        location: "60839 Liberty Rd, Joseph, OR 97846",
        shortId: "a29c76a0a0",
        priceLabel: "$429,000",
        status: "For Sale",
        acres: 5,
        tags: ["Views", "Off-grid ready", "Build ready"],
        description:
            "Experience a rare opportunity with this 5-acre mountain retreat in Joseph, Oregon, bordering the Wallowa National Forest Nestled on the north face of Mount Howard in Joseph, Oregon, this pristine 5-acre property is a true mountain sanctuary. Directly bordering 367 thousand acres of the Wallowa National Forest, it offers unparalleled access to Oregon’s most stunning wilderness, making it a dream location for outdoor enthusiasts, hunters, or those seeking privacy and seclusion. Natural Beauty & Water. This property is rich in natural water sources, with Prairie Creek and the Estes Ditch running along both the eastern and western boundaries, ensuring year-round access to fresh, flowing water. The land is teeming with abundant wildlife, from elk and deer to a variety of native bird species, creating a truly immersive natural experience.",
        features: [
            "200-amp power is installed at three separate locations, providing flexibility for future development.",
            "The property offers exceptional water security, with two perennial water features - Prairie Creek and the Estes Ditch - flowing through the land, complemented by a 123-foot well that was last tested at an impressive 25 gallons per minute for a full hour, providing multiple, reliable water sources year-round.",
            "A 1,000-gallon septic system is already in place at the designated home site, significantly reducing development costs and expediting the build process.",
            "The property holds a Wallowa County Planning Department 'Perfected' Zone Permit #14-03, offering a streamlined path for future development and ensuring compliance with county regulations.",
        ],
        image: "the_sanctuary.avif",
        galleries: {
            aerial: [],
            featured: [
                "chillaxin_station.avif",
                "shared_ground.avif",
                "more_morels.avif",
                "columbine_and_indian_paintbrush.avif",
                "fairyslipper_orchid.avif",
                "morels.avif",
                "mt_howard.avif",
                "mt_joseph.avif",
                "mt_joseph_zoomed.avif",
                "prairie_creek.avif",
                "the_sanctuary.avif",
                "OnXMaps.avif",
            ],
            indoors: [
                "galley_view.avif",
                "bow_view.avif",
                "stern_view.avif",
                "bunk_view.avif",
                "rack_view.avif",
                "rack_entertainment_view.avif",
                "head_view.avif",
            ],
            wildlife: [
                "turkeys.avif",
                "girth_brooks.avif",
                "tank.avif",
                "velvet_buck.avif",
                "white_tailed_buck.avif",
                "the_gang.avif",
                "rest_area.avif",
                "ruffed_grouse.avif",
            ],
            videos: ["estes_ditch_buck.mp4", "ultimate_sled_run.mp4"],
        },
    },

    {
        kind: "residential",
        slug: "203-e-7th-st-joseph-or",
        title: "The Joseph Townhome",
        location: "203 E 7th St, Joseph, OR 97846",
        shortId: "7a369a54ab",
        priceLabel: "$720,000",
        status: "For Sale",
        beds: 4,
        baths: 3,
        sqft: 2448,
        acres: 0.27,
        tags: ["Mountain Views", "Workshop", "Finished Basement", "Corner Lot"],
        description:
            "Situated in the heart of Joseph, Oregon, this versatile residence offers the perfect balance of mountain-town charm and functional design. Featuring panoramic views of the Wallowas, the property is defined by its spacious open-plan living, a fully finished basement, and a substantial detached shop. Whether utilized as a base for outdoor adventure or a dedicated creative workspace, this home provides an impeccable canvas for modern mountain living.",
        features: [
            "Mountain Views",
            "Workshop",
            "Finished Basement",
            "Corner Lot",
        ],
        image: "cover_image.avif",
        galleries: {
            featured: ["cover_image.avif"],
            other: ["gallery_2.avif", "gallery_3.avif", "gallery_1.avif"],
        },
    },
    {
        kind: "residential",
        slug: "high-desert-a-frame",
        title: "High Desert A-Frame",
        location: "Wallowa County, OR",
        shortId: "9d964f09fe",
        priceLabel: "$389,000",
        status: "Pending",
        beds: 1,
        baths: 1,
        sqft: 860,
        acres: 1.0,
        tags: ["A-Frame", "Short-term rental", "Hot tub pad"],
        description:
            "A compact A-Frame with a bold look, great light, and an easy layout. Built to photograph well and stay comfortable year-round.",
        features: [
            "Loft sleeping area",
            "Radiant floor heat",
            "Dedicated hot-tub pad (pre-wired)",
            "Fire-resistant exterior materials",
        ],
        image: "cover_image.avif",
        galleries: {
            featured: ["cover_image.avif"],
            other: ["gallery_3.avif", "gallery_1.avif", "gallery_2.avif"],
        },
    },
];
